import { supabase } from '../lib/supabase';

export const analyticsService = {
  // Get overall analytics summary
  async getAnalyticsSummary() {
    const { data, error } = await supabase?.rpc('get_vetrine_analytics_summary');

    if (error) throw error;

    return data?.[0] || {
      totalStores: 0,
      totalViews: 0,
      totalSaves: 0,
      totalPromotions: 0,
      totalEngagement: 0,
      avgViewsPerStore: 0,
      avgSavesPerStore: 0
    };
  },

  // Get popular categories with metrics
  async getPopularCategories() {
    const { data, error } = await supabase?.rpc('get_popular_categories');

    if (error) throw error;

    return data || [];
  },

  // Get store performance metrics
  async getStorePerformance() {
    const { data, error } = await supabase?.rpc('get_store_performance');

    if (error) throw error;

    return data?.map(store => ({
      storeId: store?.store_id,
      nomeNegozio: store?.nome_negozio,
      categoria: store?.categoria,
      zona: store?.zona,
      viewCount: parseInt(store?.view_count) || 0,
      saveCount: parseInt(store?.save_count) || 0,
      hasPromotion: store?.has_promotion,
      promotionEngagement: parseInt(store?.promotion_engagement) || 0,
      inEvidenza: store?.in_evidenza
    })) || [];
  },

  // Get engagement trends over time (last 30 days)
  async getEngagementTrends(days = 30) {
    const startDate = new Date();
    startDate?.setDate(startDate?.getDate() - days);

    // Get views trend
    const { data: viewsData, error: viewsError } = await supabase
      ?.from('vetrine_store_views')
      ?.select('viewed_at')
      ?.gte('viewed_at', startDate?.toISOString());

    if (viewsError) throw viewsError;

    // Get saves trend
    const { data: savesData, error: savesError } = await supabase
      ?.from('vetrine_store_saves')
      ?.select('saved_at')
      ?.gte('saved_at', startDate?.toISOString());

    if (savesError) throw savesError;

    // Get promotion engagement trend
    const { data: engagementData, error: engagementError } = await supabase
      ?.from('vetrine_promotion_engagement')
      ?.select('engaged_at, engagement_type')
      ?.gte('engaged_at', startDate?.toISOString());

    if (engagementError) throw engagementError;

    // Group by date
    const trendMap = {};
    
    // Process views
    viewsData?.forEach(item => {
      const date = new Date(item.viewed_at)?.toISOString()?.split('T')?.[0];
      if (!trendMap?.[date]) trendMap[date] = { date, views: 0, saves: 0, engagements: 0 };
      trendMap[date].views++;
    });

    // Process saves
    savesData?.forEach(item => {
      const date = new Date(item.saved_at)?.toISOString()?.split('T')?.[0];
      if (!trendMap?.[date]) trendMap[date] = { date, views: 0, saves: 0, engagements: 0 };
      trendMap[date].saves++;
    });

    // Process engagements
    engagementData?.forEach(item => {
      const date = new Date(item.engaged_at)?.toISOString()?.split('T')?.[0];
      if (!trendMap?.[date]) trendMap[date] = { date, views: 0, saves: 0, engagements: 0 };
      trendMap[date].engagements++;
    });

    // Convert to array and sort by date
    return Object.values(trendMap)?.sort((a, b) => new Date(a.date) - new Date(b.date));
  },

  // Get promotion performance metrics
  async getPromotionPerformance() {
    const { data, error } = await supabase
      ?.from('vetrine_promotions')
      ?.select(`
        id,
        titolo,
        discount_type,
        discount_value,
        start_date,
        end_date,
        status,
        view_count,
        click_count,
        share_count,
        conversion_count,
        store_id,
        vetrine_stores!inner(nome_negozio, categoria, zona)
      `)
      ?.eq('status', 'active')
      ?.order('view_count', { ascending: false });

    if (error) throw error;

    return data?.map(promo => ({
      id: promo?.id,
      titolo: promo?.titolo,
      storeName: promo?.vetrine_stores?.nome_negozio,
      categoria: promo?.vetrine_stores?.categoria,
      zona: promo?.vetrine_stores?.zona,
      discountType: promo?.discount_type,
      discountValue: promo?.discount_value,
      viewCount: promo?.view_count || 0,
      clickCount: promo?.click_count || 0,
      shareCount: promo?.share_count || 0,
      conversionCount: promo?.conversion_count || 0,
      ctr: promo?.view_count > 0 ? ((promo?.click_count / promo?.view_count) * 100)?.toFixed(2) : 0,
      conversionRate: promo?.click_count > 0 ? ((promo?.conversion_count / promo?.click_count) * 100)?.toFixed(2) : 0,
      startDate: promo?.start_date,
      endDate: promo?.end_date
    })) || [];
  },

  // Get real-time KPIs (last 24 hours vs previous 24 hours)
  async getRealTimeKPIs() {
    const now = new Date();
    const last24h = new Date(now.getTime() - 24 * 60 * 60 * 1000);
    const previous24h = new Date(now.getTime() - 48 * 60 * 60 * 1000);

    // Current period (last 24h)
    const { data: currentViews } = await supabase
      ?.from('vetrine_store_views')
      ?.select('id', { count: 'exact' })
      ?.gte('viewed_at', last24h?.toISOString());

    const { data: currentSaves } = await supabase
      ?.from('vetrine_store_saves')
      ?.select('id', { count: 'exact' })
      ?.gte('saved_at', last24h?.toISOString());

    const { data: currentEngagements } = await supabase
      ?.from('vetrine_promotion_engagement')
      ?.select('id', { count: 'exact' })
      ?.gte('engaged_at', last24h?.toISOString());

    // Previous period (24-48h ago)
    const { data: previousViews } = await supabase
      ?.from('vetrine_store_views')
      ?.select('id', { count: 'exact' })
      ?.gte('viewed_at', previous24h?.toISOString())
      ?.lt('viewed_at', last24h?.toISOString());

    const { data: previousSaves } = await supabase
      ?.from('vetrine_store_saves')
      ?.select('id', { count: 'exact' })
      ?.gte('saved_at', previous24h?.toISOString())
      ?.lt('saved_at', last24h?.toISOString());

    const { data: previousEngagements } = await supabase
      ?.from('vetrine_promotion_engagement')
      ?.select('id', { count: 'exact' })
      ?.gte('engaged_at', previous24h?.toISOString())
      ?.lt('engaged_at', last24h?.toISOString());

    const calculateChange = (current, previous) => {
      if (previous === 0) return current > 0 ? 100 : 0;
      return (((current - previous) / previous) * 100)?.toFixed(1);
    };

    return {
      views: {
        current: currentViews?.length || 0,
        previous: previousViews?.length || 0,
        change: calculateChange(currentViews?.length || 0, previousViews?.length || 0)
      },
      saves: {
        current: currentSaves?.length || 0,
        previous: previousSaves?.length || 0,
        change: calculateChange(currentSaves?.length || 0, previousSaves?.length || 0)
      },
      engagements: {
        current: currentEngagements?.length || 0,
        previous: previousEngagements?.length || 0,
        change: calculateChange(currentEngagements?.length || 0, previousEngagements?.length || 0)
      }
    };
  },

  // Get zone performance breakdown
  async getZonePerformance() {
    const { data, error } = await supabase
      ?.from('vetrine_stores')
      ?.select(`
        zona,
        id,
        vetrine_store_views(id),
        vetrine_store_saves(id)
      `);

    if (error) throw error;

    // Group by zone
    const zoneMap = {};
    data?.forEach(store => {
      const zona = store?.zona;
      if (!zoneMap?.[zona]) {
        zoneMap[zona] = { zona, storeCount: 0, viewCount: 0, saveCount: 0 };
      }
      zoneMap[zona].storeCount++;
      zoneMap[zona].viewCount += store?.vetrine_store_views?.length || 0;
      zoneMap[zona].saveCount += store?.vetrine_store_saves?.length || 0;
    });

    return Object.values(zoneMap)?.sort((a, b) => b?.viewCount - a?.viewCount);
  },

  // Track store view
  async trackStoreView(storeId, userId = null, sessionId = null) {
    const { error } = await supabase?.from('vetrine_store_views')?.insert({
      store_id: storeId,
      user_id: userId,
      session_id: sessionId || `session_${Date.now()}_${Math.random()}`,
      viewed_at: new Date()?.toISOString()
    });

    if (error) throw error;
  },

  // Track store save
  async trackStoreSave(storeId, userId) {
    const { error } = await supabase?.from('vetrine_store_saves')?.insert({
      store_id: storeId,
      user_id: userId,
      saved_at: new Date()?.toISOString()
    });

    if (error) throw error;
  },

  // Remove store save
  async removeStoreSave(storeId, userId) {
    const { error } = await supabase?.from('vetrine_store_saves')?.delete()?.eq('store_id', storeId)?.eq('user_id', userId);

    if (error) throw error;
  },

  // Track promotion engagement
  async trackPromotionEngagement(storeId, engagementType, userId = null) {
    const { error } = await supabase?.from('vetrine_promotion_engagement')?.insert({
      store_id: storeId,
      user_id: userId,
      engagement_type: engagementType,
      engaged_at: new Date()?.toISOString()
    });

    if (error) throw error;
  },

  // Get views by date range
  async getViewsByDateRange(startDate, endDate) {
    const { data, error } = await supabase?.from('vetrine_store_views')?.select('*')?.gte('viewed_at', startDate)?.lte('viewed_at', endDate);

    if (error) throw error;

    return data || [];
  },

  // Get engagement rate for promotions
  async getPromotionEngagementRate() {
    const { data: storesWithPromo, error: promoError } = await supabase?.from('vetrine_stores')?.select('id')?.not('promo_titolo', 'is', null)?.neq('promo_titolo', '');

    if (promoError) throw promoError;

    const totalPromotions = storesWithPromo?.length || 0;

    const { data: engagements, error: engagementError } = await supabase?.from('vetrine_promotion_engagement')?.select('store_id', { count: 'exact' });

    if (engagementError) throw engagementError;

    const totalEngagements = engagements?.length || 0;

    return {
      totalPromotions,
      totalEngagements,
      engagementRate: totalPromotions > 0 ? ((totalEngagements / totalPromotions) * 100)?.toFixed(2) : 0
    };
  }
};